import type { ReactNode } from "react";
import Markdown from "react-markdown";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { SourceAttribution } from "@/types/paper";

const CITATION_RE = /(\[\d+\])/g;

interface Props {
  content: string;
  sources: SourceAttribution[];
}

export function CitationText({ content, sources }: Props) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <Markdown
        components={{
          p: ({ children }) => (
            <p>{injectCitations(children, sources)}</p>
          ),
          li: ({ children }) => (
            <li>{injectCitations(children, sources)}</li>
          ),
          pre: ({ children }) => (
            <pre className="not-prose rounded bg-muted p-2 text-xs overflow-x-auto">
              {children}
            </pre>
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}

/**
 * Walk ReactNode children, splitting any string segments on [N] citation
 * patterns and replacing matches with CitationBadge popovers.
 */
function injectCitations(
  children: ReactNode,
  sources: SourceAttribution[],
): ReactNode {
  if (!Array.isArray(children)) {
    return processNode(children, sources);
  }
  return children.map((child, i) => (
    <span key={i}>{processNode(child, sources)}</span>
  ));
}

function processNode(
  node: ReactNode,
  sources: SourceAttribution[],
): ReactNode {
  if (typeof node !== "string") return node;

  const parts = node.split(CITATION_RE);
  if (parts.length === 1) return node;

  return parts.map((part, i) => {
    const match = /^\[(\d+)\]$/.exec(part);
    if (!match) return part;
    const num = Number(match[1]);
    const source = sources.find((s) => s.number === num);
    if (!source) return part;
    return <CitationBadge key={i} source={source} />;
  });
}

function CitationBadge({ source }: { source: SourceAttribution }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer font-medium text-primary underline decoration-dotted"
        >
          [{source.number}]
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 space-y-2 p-3">
        <p className="text-sm font-bold leading-tight">{source.title}</p>
        <p className="text-xs text-muted-foreground">
          {source.authors} ({source.year ?? "n.d."})
        </p>
        {source.excerpt && (
          <div className="rounded bg-muted p-2 text-xs text-muted-foreground">
            {source.excerpt}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
